/**
 * Onboarding Redux Slice
 *
 * Manages onboarding wizard state:
 * - Step tracking (demo-data, invite-team, integrations, complete)
 * - Demo data seeding status
 * - Team invitations
 * - Integration setup
 * - Completion status
 */

import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Onboarding flow
  currentStep: 'demo-data', // demo-data, invite-team, integrations, complete
  isComplete: false,

  // Demo Data
  demoDataLoading: false,
  demoDataLoaded: false,
  demoDataError: null,
  demoDataIncluded: false,

  // Team Invitations
  teamInvitations: [],
  invitationLoading: false,
  invitationError: null,

  // Integrations (optional)
  integrationsConfigured: false,
  integrationsLoading: false,
  integrationsError: null,

  // Completion
  completionLoading: false,
  completionError: null,
};

const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    // Step Management
    setCurrentStep: (state, action) => {
      state.currentStep = action.payload;
    },

    // Demo Data
    setDemoDataLoading: (state, action) => {
      state.demoDataLoading = action.payload;
    },
    setDemoDataLoaded: (state, action) => {
      state.demoDataLoaded = action.payload;
    },
    setDemoDataError: (state, action) => {
      state.demoDataError = action.payload;
    },
    setDemoDataIncluded: (state, action) => {
      state.demoDataIncluded = action.payload;
    },

    // Team Invitations
    addTeamInvitation: (state, action) => {
      state.teamInvitations.push(action.payload);
    },
    removeTeamInvitation: (state, action) => {
      state.teamInvitations = state.teamInvitations.filter((inv) => inv.id !== action.payload);
    },
    setInvitationLoading: (state, action) => {
      state.invitationLoading = action.payload;
    },
    setInvitationError: (state, action) => {
      state.invitationError = action.payload;
    },
    clearInvitations: (state) => {
      state.teamInvitations = [];
    },

    // Integrations
    setIntegrationsConfigured: (state, action) => {
      state.integrationsConfigured = action.payload;
    },
    setIntegrationsLoading: (state, action) => {
      state.integrationsLoading = action.payload;
    },
    setIntegrationsError: (state, action) => {
      state.integrationsError = action.payload;
    },

    // Completion
    setCompletionLoading: (state, action) => {
      state.completionLoading = action.payload;
    },
    setCompletionError: (state, action) => {
      state.completionError = action.payload;
    },
    markOnboardingComplete: (state) => {
      state.isComplete = true;
      state.currentStep = 'complete';
    },

    // Reset
    resetOnboarding: (_state) => {
      return initialState;
    },
  },
});

export const {
  setCurrentStep,
  setDemoDataLoading,
  setDemoDataLoaded,
  setDemoDataError,
  setDemoDataIncluded,
  addTeamInvitation,
  removeTeamInvitation,
  setInvitationLoading,
  setInvitationError,
  clearInvitations,
  setIntegrationsConfigured,
  setIntegrationsLoading,
  setIntegrationsError,
  setCompletionLoading,
  setCompletionError,
  markOnboardingComplete,
  resetOnboarding,
} = onboardingSlice.actions;

// Selectors
export const selectOnboardingCurrentStep = (state) => state.onboarding.currentStep;
export const selectOnboardingIsComplete = (state) => state.onboarding.isComplete;
export const selectDemoDataLoading = (state) => state.onboarding.demoDataLoading;
export const selectDemoDataLoaded = (state) => state.onboarding.demoDataLoaded;
export const selectDemoDataError = (state) => state.onboarding.demoDataError;
export const selectDemoDataIncluded = (state) => state.onboarding.demoDataIncluded;
export const selectTeamInvitations = (state) => state.onboarding.teamInvitations;
export const selectInvitationLoading = (state) => state.onboarding.invitationLoading;
export const selectInvitationError = (state) => state.onboarding.invitationError;
export const selectIntegrationsConfigured = (state) => state.onboarding.integrationsConfigured;
export const selectCompletionLoading = (state) => state.onboarding.completionLoading;
export const selectCompletionError = (state) => state.onboarding.completionError;

export default onboardingSlice.reducer;
